import React, { useContext } from 'react'
import { Formik, Form as FormikForm } from 'formik'

import { BudgetContext } from './context'
import { submitSimpleBudget, submitDetailedBudget } from './effects/formSubmit'
import { R } from '@utils/currency'

/* The SelectedTabForm wraps it's children in the correct form context,
 * simple or detailed. This is because the entire component is in fact a form.
 * It will also allow us to put different parts of the form in different areas
 * of the component. I.e. Some inputs in the content, and a submit button in the
 * footer.
 */
export const SelectedTabForm = ({ children, ...props }) => {
  const { state: { selectedTab } } = useContext(BudgetContext)
  const formInitialValues = initialValues(
    props.budget,
    props.isRetired,
    props.totalIncomeFromInvestments,
    props.totalLiabilityRepayments,
    props.totalAdditionalIncomeFromInvestments,
    props.totalContributions,
    props.totalCurrentNetExpense,
    props.totalCurrentNetIncome,
  )

  return (
    selectedTab === 0
    ? <SimpleBudgetForm children={children} initialValues={formInitialValues} />
    : <DetailedBudgetForm children={children} initialValues={formInitialValues} />
  )
}

export const SimpleBudgetForm = ({ children, initialValues }) => {
  const {
    state: { budget, simpleFormRef, updateBudgetHandler },
    actions: { setLoading }
  } = useContext(BudgetContext)
  const { detailed_budget } = budget

  return (
    <Formik
      enableReinitialize
      innerRef={simpleFormRef}
      onSubmit={async (values) => {
        setLoading(true)
        await submitSimpleBudget(values, detailed_budget, updateBudgetHandler)
        setLoading(false)
      }}
      initialValues={initialValues}>
      <FormikForm className='app-form'>
        { children }
      </FormikForm>
    </Formik>
  )
}

export const DetailedBudgetForm = ({ children, initialValues }) => {
  const {
    state: { detailedFormRef, updateBudgetHandler },
    actions: { setLoading }
  } = useContext(BudgetContext)

  return (
    <Formik
      enableReinitialize
      innerRef={detailedFormRef}
      onSubmit={async (formValues) => {
        setLoading(true)
        await submitDetailedBudget(formValues, updateBudgetHandler)
        setLoading(false)
      }}
      initialValues={initialValues}
    >
      <FormikForm className='app-form'>
        { children }
      </FormikForm>
    </Formik>
  )
}

/* Not all budgets have the key 'use_current_expense_for_retirement'.
 * If it is not present, default it to false.
 *
 * Similarly, not all budgets have 'gross_monthly_current_income'
 * It should default to 0
 */
const initialValues = (budget, isRetired, totalIncomeFromInvestments, totalLiabilityRepayments, totalAdditionalIncomeFromInvestments, totalContributions, totalCurrentNetExpense, totalCurrentNetIncome) => ({
  ...budget,
  use_current_expense_for_retirement: isRetired || budget.use_current_expense_for_retirement || false,
  gross_monthly_current_income: budget.gross_monthly_current_income || null,
  totalIncomeFromInvestments: totalIncomeFromInvestments || 0,
  totalLiabilityRepayments: R(totalLiabilityRepayments).value || 0,
  totalAdditionalIncomeFromInvestments: R(totalAdditionalIncomeFromInvestments).value || 0,
  totalContributions: R(totalContributions).value || 0,
  totalCurrentNetExpense: R(totalCurrentNetExpense).value || 0,
  totalCurrentNetIncome: R(totalCurrentNetIncome).value || 0
})
